import { Address as _Address } from '@/gql/graphql';
import {
  Button,
  ConditionalWrapper,
  Alert,
  Field,
  Input,
  Modal,
  Text,
} from '@payaca/ambrosia';

import { getRegionalTextString } from '@payaca/helpers/internationalHelper';
import { RegionalStrings } from '@payaca/types/internationalTypes';
import ValidatedForm, {
  TRenderFormContents,
} from '@payaca/components/validatedForm/ValidatedForm';
import { FC, useCallback, useMemo } from 'react';
import { useRegion } from '@/api/rest/users/useRegion';

type Address = Partial<_Address>;

export interface IProps {
  address?: Address;
  // Memoize this function
  onSave?: (address: Address) => void;
  saveButtonText?: string;
  onClose?: () => void;
  isInModal?: boolean;
  isSiteAddress?: boolean;
}

type TFormState = Address;

const ManuallyAddEditAddressControl: FC<IProps> = (props) => {
  const {
    address,
    onSave,
    saveButtonText = 'Save',
    onClose,
    isInModal = false,
    isSiteAddress = false,
  } = props;

  const region = useRegion();
  const hasManualPin = address?.coordinatesSource === 'MANUAL';

  const initialFormState = useMemo<TFormState>(
    () => ({ ...address }),
    [address]
  );

  const renderFormContents = useCallback<TRenderFormContents<TFormState>>(
    (
      isValid,
      formState,
      validationState,
      touchedState,
      onFieldChange,
      onFieldTouch
    ) => {
      return (
        <>
          <ConditionalWrapper condition={isInModal} Wrapper={Modal.Body}>
            <div className="flex flex-col gap-4">
              {hasManualPin && (
                <Alert colour="yellow">
                  <Text>
                    {isSiteAddress
                      ? 'Site pin adjusted manually'
                      : 'Address location adjusted manually'}
                  </Text>
                  <Text>
                    {isSiteAddress
                      ? 'If this edit changes the site location, the pin will be reset using the new address.'
                      : 'If this edit changes the location, the pin will be reset using the new address.'}
                  </Text>
                </Alert>
              )}
              <Field.Legacy name="line1">
                <Field.Label>Address line 1</Field.Label>
                <Input
                  value={formState.line1 || ''}
                  onChange={(value) => onFieldChange({ line1: value })}
                />
              </Field.Legacy>
              <Field.Legacy name="line2">
                <Field.Label>Address line 2</Field.Label>
                <Input
                  value={formState.line2 || ''}
                  onChange={(value) => onFieldChange({ line2: value })}
                />
              </Field.Legacy>
              <Field.Legacy name="city">
                <Field.Label>Town / city</Field.Label>
                <Input
                  value={formState.city || ''}
                  onChange={(value) => onFieldChange({ city: value })}
                />
              </Field.Legacy>
              <Field.Legacy name="administrativeArea">
                <Field.Label>
                  {getRegionalTextString(
                    region,
                    RegionalStrings.ADMINISTRATIVE_AREA
                  )}
                </Field.Label>
                <Input
                  value={formState.administrativeArea || ''}
                  onChange={(value) =>
                    onFieldChange({ administrativeArea: value })
                  }
                />
              </Field.Legacy>
              <Field.Legacy name="postcode">
                <Field.Label>
                  {getRegionalTextString(region, RegionalStrings.POSTCODE)}
                </Field.Label>
                <Input
                  value={formState.postalCode || ''}
                  onChange={(value) => onFieldChange({ postalCode: value })}
                />
              </Field.Legacy>
            </div>
          </ConditionalWrapper>
          <ConditionalWrapper condition={isInModal} Wrapper={Modal.Footer}>
            <ConditionalWrapper
              condition={isInModal}
              Wrapper={Modal.Footer.Actions}
            >
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">{saveButtonText}</Button>
            </ConditionalWrapper>
          </ConditionalWrapper>
        </>
      );
    },
    [saveButtonText, onClose, isInModal, region, hasManualPin, isSiteAddress]
  );

  return (
    <ValidatedForm<TFormState>
      initialFormState={initialFormState}
      renderFormContents={renderFormContents}
      onFormSubmit={onSave}
    />
  );
};

export default ManuallyAddEditAddressControl;
